import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import {
  getTicketList,
  updateTicket,
  getCategoryList,
  getAdminList,
  createTicket,
  escalateTicket,
} from "@/services/ticketManagement";

function ensureNameObj(obj: any): any {
  if (obj == null) return { name: "—" };
  if (typeof obj !== "object") return { name: "—" };
  const name =
    (obj.name && String(obj.name).trim()) ||
    (obj.first != null && obj.last != null
      ? [obj.first, obj.last].filter(Boolean).join(" ").trim()
      : "") ||
    (obj.email && String(obj.email)) ||
    "—";
  return { ...obj, name: name || "—" };
}

function getCurrentUserAsUpdatedBy(rootState: any): any {
  const auth = (rootState && rootState.auth) || {};
  const nameObj = auth.name || {};
  const name =
    (auth.name && typeof auth.name === "string" && auth.name.trim()) ||
    (nameObj.first != null && nameObj.last != null
      ? [nameObj.first, nameObj.last].filter(Boolean).join(" ").trim()
      : "") ||
    (auth.userName && String(auth.userName).trim()) ||
    (auth.email && String(auth.email)) ||
    "—";
  return {
    id: auth._id || auth.id,
    name: name || "—",
    email: auth.email || "",
    phone: auth.phone || "",
    userType: auth.userType || auth.userRole || "EMPLOYEE",
  };
}

@Module({
  namespaced: true,
})
export default class TicketManagement extends VuexModule {
  // ================= STATE =================
  ticketList: any[] = [];
  categoryList: any[] = [];
  adminList: any[] = [];

  // ================= MUTATIONS =================
  @Mutation
  public setTicketList(data: any[]) {
    this.ticketList = data;
  }

  @Mutation
  public setCategoryList(data: any[]) {
    this.categoryList = data;
  }

  @Mutation
  public setAdminList(data: any[]) {
    this.adminList = data;
  }

  @Mutation
  public setSelectedTicket(ticket: any) {
    const list = [...this.ticketList];
    const idx = list.findIndex((t) => t._id === (ticket && ticket._id));
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...ticket };
      this.ticketList = list;
    }
  }

  // ================= ACTIONS =================

  @Action
  public async fetchTicketList() {
    try {
      const response = await getTicketList();
      const raw =
        (response && response.data) ||
        (response && response.response) ||
        response;

      const list =
        Array.isArray(raw)
          ? raw
          : raw && raw.response && Array.isArray(raw.response)
            ? raw.response
            : [];

      this.context.commit("setTicketList", list);
      return response;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async updateTicketAction(payload: { id: string; data: any }) {
    try {
      const response = await updateTicket(payload.id, payload.data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async updateTicket(payload: {
    id: string;
    status?: string;
    assignedTo?: string;
    severity?: string;
    rawTicket?: any;
  }) {
    const state = this.context.state as any;
    const list = state.ticketList || [];
    const ticket = list.find((t: any) => t._id === payload.id) || payload.rawTicket;
    if (!ticket) throw new Error("Ticket not found");
    const raw = { ...ticket } as any;
    const apiStatus =
      payload.status === "NEW" ? "OPEN" : payload.status || raw.status;
    if (payload.status != null) raw.status = apiStatus;
    if (payload.assignedTo != null) {
      const adminList = state.adminList || [];
      const admin = adminList.find((a) => a._id === payload.assignedTo);
      if (admin) {
        const nameObj = admin.name || {};
        const name =
          [nameObj.first, nameObj.last].filter(Boolean).join(" ") ||
          (admin.authentication && admin.authentication.userName) ||
          "";
        raw.assignTo = {
          id: admin._id,
          name,
          email: (admin.authentication && admin.authentication.email) || "",
        };
      }
    }
    if (payload.severity != null) raw.severity = payload.severity;
    const worknoteHistory = (raw.worknoteHistory || []).map((item: any) =>
      item && item.updatedBy
        ? { ...item, updatedBy: ensureNameObj(item.updatedBy) }
        : item
    );
    const changeHistory = (raw.changeHistory || []).map((item: any) =>
      item && item.updatedBy
        ? { ...item, updatedBy: ensureNameObj(item.updatedBy) }
        : item
    );
    const rootState = this.context.rootState;
    const currentUser = getCurrentUserAsUpdatedBy(rootState);
    const updatePayload = {
      ...raw,
      _id: payload.id,
      status: apiStatus,
      reportedBy: ensureNameObj(raw.reportedBy),
      assignTo: raw.assignTo || ensureNameObj(raw.assignTo),
      reportedTo: ensureNameObj(raw.reportedTo),
      updatedBy: currentUser,
      worknoteHistory,
      changeHistory,
    };
    await this.context.dispatch("updateTicketAction", {
      id: payload.id,
      data: updatePayload,
    });
    this.context.commit("setSelectedTicket", updatePayload);
    await this.context.dispatch("fetchTicketList");
    return { success: true };
  }

  @Action
  public async escalateTicketAction(payload: {
    id: string;
    assignCategory: string;
    officeHierarchy?: string;
    assignTo: { id: string; name: string; email: string; phone: string; userType: string };
    escalationReason: string;
    escalationLevel: number;
  }) {
    const rootState = this.context.rootState;
    const updatedBy = getCurrentUserAsUpdatedBy(rootState);
    const body = {
      assignCategory: payload.assignCategory,
      officeHierarchy: payload.officeHierarchy || "",
      assignTo: payload.assignTo,
      escalationReason: payload.escalationReason,
      escalationLevel: payload.escalationLevel,
      updatedBy: {
        name: updatedBy.name,
        email: updatedBy.email,
        phone: updatedBy.phone || "",
      },
    };
    const response = await escalateTicket(payload.id, body);
    await this.context.dispatch("fetchTicketList");
    return response;
  }

  @Action
  public async fetchTeamList() {
    return this.context.dispatch("fetchAdminList", { limit: 100, page: 1 });
  }

  @Action
  public async fetchCategoryList() {
    try {
      const response = await getCategoryList();
      const raw =
        (response && response.data) ||
        (response && response.response) ||
        response;

      const list =
        Array.isArray(raw)
          ? raw
          : raw && raw.response && Array.isArray(raw.response)
            ? raw.response
            : [];

      this.context.commit("setCategoryList", list);
      return response;
    } catch (error) {
      throw error;
    }
  }

  @Action
  public async fetchAdminList(params: any) {
    try {
      const response = await getAdminList(params);
      const raw =
        (response && response.data) ||
        (response && response.response) ||
        response;

      const list =
        Array.isArray(raw)
          ? raw
          : raw && raw.response && Array.isArray(raw.response)
            ? raw.response
            : [];

      this.context.commit("setAdminList", list);
      return response;
    } catch (error) {
      throw error;
    }
  }

  //  ================= CREATE TICKET ACTION =================
  @Action
  public async createTicketAction(payload: any) {
    try {
      const response = await createTicket(payload);
      await this.context.dispatch("fetchTicketList");
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ================= GETTERS =================
  get GetTicketList() {
    return this.ticketList;
  }

  get GetCategoryList() {
    return this.categoryList;
  }

  get GetAdminList() {
    return this.adminList;
  }

  get getSelectedTicket() {
    return (id: string) => {
      const list = this.ticketList || [];
      return list.find((t) => t._id === id) || null;
    };
  }
}
